import { useEffect, useMemo } from 'react';
import { Order_Enum, SortBy_Enum } from '@weave/schema-gen-ts/dist/schemas/schedule/quickfill/v2/quickfill.pb';
import { ScheduleV2 } from '@frontend/api-schedule-v2';
import { useWaitlistShallowStore } from '../stores/waitlist.store';

type Request = Omit<ScheduleV2.Types.ListIO['input'], 'skip' | 'limit'>;

export const useGetWaitlistInfiniteQuery = (request: Request) => {
  return ScheduleV2.Queries.useListInfiniteQuery({
    request: {
      locationId: request.locationId,
      search: request.search,
      sortBy: request.sortBy || SortBy_Enum.CREATED_AT,
      order: request.order || Order_Enum.DESC,
    },
    options: {
      enabled: !!request.locationId,
      select: (data) => {
        return {
          ...data,
          pages: data.pages.map((page) => ({
            rows: page.data ?? [],
            totalCount: page.meta?.totalCount ?? 0,
          })),
        };
      },
    },
  });
};

export const useGetWaitlistInfiniteQueryWithCustomData = (request: Request) => {
  const infiniteQuery = useGetWaitlistInfiniteQuery(request);
  const { locationWiseTotalCounts, setLocationWiseTotalCount } = useWaitlistShallowStore(
    'setLocationWiseTotalCount',
    'locationWiseTotalCounts'
  );

  const firstPageInfo = infiniteQuery.data?.pages[0];
  const isEmptyList = firstPageInfo?.rows?.length === 0;
  const totalCount = firstPageInfo?.totalCount || 0;

  useEffect(() => {
    if (infiniteQuery.isLoading || request.search || !request.locationId) return;

    // Update total count in store on initial load or when search term is not present
    setLocationWiseTotalCount(request.locationId, totalCount);
  }, [infiniteQuery.isLoading, request.locationId, request.search, setLocationWiseTotalCount, totalCount]);

  const { optedInPersonIds, firstPersonId } = useMemo(() => {
    const optedInIds: string[] = [];
    let firstId = '';

    infiniteQuery.data?.pages.forEach((page) => {
      page.rows?.forEach((row) => {
        const personId = row.personId;
        if (personId) {
          firstId = firstId || personId;
          if (row.person?.receiveSms !== false) {
            optedInIds.push(personId);
          }
        }
      });
    });

    return {
      optedInPersonIds: optedInIds,
      firstPersonId: firstId,
    };
  }, [infiniteQuery.data]);

  return {
    infiniteQuery,
    isEmptyList,
    optedInPersonIds,
    firstPersonId,
    totalCount: request.locationId ? locationWiseTotalCounts[request.locationId] || 0 : 0,
  };
};
